import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaCreditCard,
  FaCheckCircle,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaPlus,
  FaSpinner,
  FaEuroSign,
  FaClock,
  FaMoneyBillWave,
  FaReceipt,
} from 'react-icons/fa';
import PageHeader from '../../components/misc/PageHeader';
import Button from '../../components/buttons/Button';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import { CheckOnboardingDto, PayoutDetails, PayoutsMetadata } from '../../../services/model/marketplace.model';
import {
  checkOnboarding,
  createMerchantAccount,
  getAccountBalance,
  getLoginToDashBoardLoginLink,
} from '../../../services/merchant.marketplace.service';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
} from '../../components/tables/BaseTable';
import { formatEuroCentsWithSign } from '../../../utils/bankAccountUtil';
import { getPayoutTransactionsForBusiness } from '../../../services/orders.marketplace.service';

interface MerchantAccountPageProps {
  hideHeader?: boolean;
}
export default function MerchantAccountPage({
  hideHeader = false,
}: MerchantAccountPageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.merchantAccount' });
  const navigate = useNavigate();
  const [onboardStatus, setOnboardStatus] = useState<CheckOnboardingDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState<PayoutDetails[]>([]);
  const [payoutsMetadata, setPayoutsMetadata] = useState<PayoutsMetadata>({
    totalPayouts: 0,
    totalAmount: 0,
    totalFees: 0,
    totalIncomingTransactions: 0,
  });


  useEffect(() => {
    loadOnboardingStatus().then(() => {
      loadPayoutTransactions();
      loadIncomingPayouts();
    });
  }, []);

  const loadIncomingPayouts = async () => {
    try {
      const balance = await getAccountBalance();
      setPayoutsMetadata((prev) => ({ ...prev, totalIncomingTransactions: balance }));
    } catch (error) {
      console.error('Error loading incoming payouts:', error);
    }
  }
  
  const loadPayoutTransactions = async () => {
    try {
      setLoading(true);
      const transactions = await getPayoutTransactionsForBusiness();
      console.log('Fetched payout transactions:', transactions);
      const payouts = transactions.reduce((acc: Record<string, PayoutDetails>, tx) => {
        if (!acc[tx.payoutId]) {
          acc[tx.payoutId] = {
            id: tx.payoutId,
            date: tx.payoutDate.toString(),
            totalAmount: 0,
            totalIncomingTransactions: 0,
            totalFees: 0,
            numberOfTransactions: 0,
          };
        }
        acc[tx.payoutId].totalAmount += tx.netAmount;
        acc[tx.payoutId].totalIncomingTransactions += tx.faceValueAmount;
        acc[tx.payoutId].totalFees += (tx.stripeFeeAmount ?? 0) + (tx.veraFeeAmount ?? 0);
        acc[tx.payoutId].numberOfTransactions += 1;
        return acc;
      }, {});

      setPayoutHistory(Object.values(payouts).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
      const totalAmount = Object.values(payouts).reduce((sum, p) => sum + p.totalAmount, 0);
      const totalFees = Object.values(payouts).reduce((sum, p) => sum + p.totalFees, 0);
      
      setPayoutsMetadata((prev) => ({
        ...prev,
        totalAmount: totalAmount - totalFees,
        totalFees,
        totalPayouts: totalAmount,
      }));
    } catch (error) {
      console.error('Error loading payout transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOnboardingStatus = async () => {
    try {
      setLoading(true);
      const status = await checkOnboarding();
      setOnboardStatus(status);
    } catch (error) {
      console.error('Error loading onboarding status:', error);
      setOnboardStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMerchantAccount = async () => {
    try {
      setCreatingAccount(true);
      const status = await createMerchantAccount();
      setOnboardStatus(status);
      toast.success('Merchant account creation initiated successfully');
    } catch (error) {
      console.error('Error creating merchant account:', error);
      toast.error('Failed to create merchant account. Please try again.');
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleLoginToDashBoard = async () => {
    try {
      const response = await getLoginToDashBoardLoginLink();
      window.open(response.url, '_blank');
    } catch (error) {
      console.error('Error getting dashboard login link:', error);
      toast.error('Failed to access dashboard. Please try again.');
    }
  };

  const handleContinueOnboarding = () => {
    if (onboardStatus?.onboardingLink) {
      window.location.href = onboardStatus.onboardingLink;
    }
  };

  const handlePayoutRowClick = (payoutId: string) => {
    navigate(`/financial/payout/${payoutId}`);
  };

  const getStatusCard = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-3">
            <FaSpinner className="text-primary-600 h-6 w-6 animate-spin" />
            <span className="text-lg text-gray-600">Loading merchant account status...</span>
          </div>
        </div>
      );
    }

    if (!onboardStatus) {
      return (
        <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
          <div className="text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <FaExclamationTriangle className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">No Merchant Account</h3>
            <p className="mx-auto mb-6 max-w-md text-gray-600">
              You need to create a merchant account to process payments and manage your sales.
            </p>
            <PrimaryButton
              text={creatingAccount ? 'Creating Account...' : 'Create Merchant Account'}
              icon={creatingAccount ? FaSpinner : FaPlus}
              onClick={handleCreateMerchantAccount}
              disabled={creatingAccount}
              className="min-w-[200px]"
            />
          </div>
        </div>
      );
    }

    if (!onboardStatus.onboarded) {
      return (
        <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
          <div className="text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
              <FaExclamationTriangle className="h-8 w-8 text-yellow-600" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Onboarding Required</h3>
            <p className="mx-auto mb-6 max-w-md text-gray-600">
              Your merchant account has been created but requires additional setup to be fully
              activated.
            </p>
            <PrimaryButton
              text="Continue Onboarding"
              icon={FaExternalLinkAlt}
              onClick={handleContinueOnboarding}
              className="min-w-[200px]"
            />
          </div>
        </div>
      );
    }

    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
        <div className="text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <FaCheckCircle className="h-8 w-8 text-success" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">Merchant Account Active</h3>
          <p className="mx-auto mb-6 max-w-md text-gray-600">
            Your merchant account is fully set up and ready to process payments.
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <PrimaryButton
              text="Access Dashboard"
              icon={FaExternalLinkAlt}
              onClick={handleLoginToDashBoard}
              className="min-w-[200px]"
            />
            <Button
              variant="tertiary"
              onClick={() => console.log(onboardStatus)}
              className="min-w-[200px]">
              View Account Details
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {!hideHeader && <PageHeader title={t('title')} subtitle={t('subtitle')} />}

      {/* Status Overview */}
      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">Account Status</h2>
        {getStatusCard()}
      </div>



      {/* Payout Overview Cards */}
      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">Payout Overview</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Total Payout */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaEuroSign className="h-6 w-6 text-primary-300" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Payout</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatEuroCentsWithSign(payoutsMetadata.totalPayouts - payoutsMetadata.totalFees, i18n.language)}
                </p>
              </div>
            </div>
          </div>

          {/* Pending Payout */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaClock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Pending Payout</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatEuroCentsWithSign(payoutsMetadata.totalIncomingTransactions, i18n.language)}
                </p>
              </div>
            </div>
          </div>

          {/* Collected */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaMoneyBillWave className="h-6 w-6 text-success" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Collected</p>
                <p className="text-xl font-bold text-success">
                  {formatEuroCentsWithSign(payoutsMetadata.totalPayouts, i18n.language)}
                </p>
              </div>
            </div>
          </div>

          {/* Fees Paid */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaReceipt className="h-6 w-6 text-danger" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Fees Paid</p>
                <p className="text-xl font-bold text-danger">
                  {formatEuroCentsWithSign(payoutsMetadata.totalFees, i18n.language)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payout History Table */}
      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">Payout History</h2>
        <BaseTable>
          <BaseTableHeader>
            <BaseTableHeaderRow>
              <th className="text-left">Date</th>
              <th className="text-right">Total Amount [€]</th>
              <th className="text-right">Total Amount [€] Incoming Transactions</th>
              <th className="text-right">Total Fees [€]</th>
              <th className="text-center">Number of Incoming Transactions</th>
            </BaseTableHeaderRow>
          </BaseTableHeader>
          <BaseTableBody>
            {payoutHistory.map((payout, index) => (
              <BaseTableDataRow
                key={index}
                className="group cursor-pointer transition-all hover:bg-secondary-200"
                onClick={() => handlePayoutRowClick(payout.id)}>
                <td className="py-3 text-left">
                  {new Date(payout.date).toLocaleDateString(i18n.language)}
                </td>
                <td className="py-3 text-right">
                  <span className="font-medium text-gray-900">
                    {formatEuroCentsWithSign(payout.totalAmount, i18n.language)}
                  </span>
                </td>
                <td className="py-3 text-right">
                  <span className="font-medium text-success">
                    {formatEuroCentsWithSign(payout.totalIncomingTransactions, i18n.language)}
                  </span>
                </td>
                <td className="py-3 text-right">
                  <span className="font-medium text-danger">
                    {formatEuroCentsWithSign(payout.totalFees, i18n.language)}
                  </span>
                </td>
                <td className="py-3 text-center">
                  <span className="font-medium text-gray-900">{payout.numberOfTransactions}</span>
                </td>
              </BaseTableDataRow>
            ))}
          </BaseTableBody>
        </BaseTable>
      </div>
    </div>
  );
}
